<script setup lang="ts">
// components
import SvgIcon from '~/components/SvgIcon.vue';

// composables
import useLocalePathWithSlash from '~/composables/useLocalePathWithSlash';

// constants
import { PATHS } from '~/constants/nav-items';

// utils
import omit from 'lodash.omit';
import { isString, isLast, removeLocaleFromPath } from '~/utils';

type Breadcrumb = string | Record<'i18nKey' | 'path', string>;

type WithAppend = { append: true; items: Breadcrumb[]; overwrite?: never };
type WithOverwrite = { overwrite: true; items: Breadcrumb[]; append?: never };
type WithDefault = { append?: never; overwrite?: never; items?: never };

type AppBreadcrumbsProps = { accent?: 'light' | 'dark' } & (
  | WithAppend
  | WithOverwrite
  | WithDefault
);

const props = withDefaults(defineProps<AppBreadcrumbsProps>(), {
  accent: 'dark',
  items: () => [],
});

const { pathname } = useRequestURL();
const localePathWithSlash = useLocalePathWithSlash();

function generateBreadcrumbs() {
  const PATHS_VALUES = Object.values(PATHS);

  return removeLocaleFromPath(pathname)
    .split('/')
    .reduce<Breadcrumb[]>((acc, pathPiece) => {
      if (pathPiece.length) {
        const link = PATHS_VALUES.find(({ path }) => path.includes(pathPiece));

        if (link) {
          acc.push({ i18nKey: link.i18nKey, path: link.path });
        }
      }

      return acc;
    }, []);
}

const breadcrumbs = ref<Breadcrumb[]>([]);

watch(
  Object.values(omit(toRefs(props), 'accent')),
  ([append, incoming, overwrite]) => {
    const generated = generateBreadcrumbs();
    const result = [];

    if (append) {
      result.push(...generated, ...incoming);
    } else if (overwrite) {
      result.push(...incoming);
    } else {
      result.push(...generated);
    }

    breadcrumbs.value = result.filter(Boolean);
  },
  {
    immediate: true,
  },
);
</script>

<template>
  <ul :class="accent" data-component-name="AppBreadcrumbs">
    <li>
      <NuxtLink :to="localePathWithSlash('/')">
        <SvgIcon name="home" />
      </NuxtLink>
    </li>

    <template v-for="(breadcrumb, index) of breadcrumbs" :key="index">
      <li>
        <SvgIcon name="chevron-right" />
      </li>

      <li class="nowrap">
        <span v-if="isLast(breadcrumbs, index)">
          {{
            (isString(breadcrumb) && breadcrumb) ||
            (isObject(breadcrumb) && $t(breadcrumb.i18nKey))
          }}
        </span>

        <NuxtLink
          v-else-if="isObject(breadcrumb)"
          :to="localePathWithSlash(breadcrumb.path)"
        >
          {{ $t(breadcrumb.i18nKey) }}
        </NuxtLink>
      </li>
    </template>
  </ul>
</template>

<style scoped lang="scss">
@use 'sass:map';
@use '_/mixins/size';
@use '_/mixins/typo';
@use '_/mixins/flex';
@use '_/mixins/media';
@use '_/variables/color-roles';
@use '_/fn';

ul[data-component-name='AppBreadcrumbs'] {
  @include flex.center-start;

  gap: 0.25rem;
  overflow-x: auto;

  li {
    @include flex.center-center;

    a {
      @include flex.center-center;
      @include typo.caption;

      font-weight: 500;
      text-decoration-line: underline;
    }

    svg {
      @include size.fixed(1rem);
    }

    span {
      @include typo.caption;
    }
  }

  &.dark {
    li {
      a {
        color: map.get(color-roles.$links, 'Blue-90');
      }

      svg {
        fill: fn.token('text-tertiary');
      }

      span {
        color: fn.token('text-tertiary');
      }
    }
  }

  &.light {
    li {
      a {
        color: map.get(color-roles.$links, 'Blue-40');

        svg {
          fill: fn.token('icon-primary');
        }
      }

      > svg {
        fill: fn.token('icon-secondary');
      }

      span {
        color: fn.token('text-secondary');
      }
    }
  }
}
</style>
